import { createSlice, createAsyncThunk, isPending, isFulfilled, isRejected } from '@reduxjs/toolkit';
import axios from 'axios';

interface S3FileState {
  loading: boolean;
  errorMessage: string | null;
  updateSuccess: boolean;
  filePath: string | null;
  fileBlob: Blob | null;
}

const initialState: S3FileState = {
  loading: false,
  errorMessage: null,
  updateSuccess: false,
  filePath: null,
  fileBlob: null,
};

// Async Thunks
export const uploadFileToS3 = createAsyncThunk<void, { formData: FormData }>('s3File/upload', async ({ formData }, thunkAPI) => {
  try {
    const response = await axios.post('/api/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { uploadPath: 'invoice' },
    });
    console.warn('File uploaded successfully:', response.data);
  } catch (error) {
    return thunkAPI.rejectWithValue('Upload failed');
  }
});

export const downloadFileFromS3 = createAsyncThunk<Blob, { folderPath: string; fileName: string }>(
  's3File/download',
  async ({ folderPath, fileName }, thunkAPI) => {
    try {
      const response = await axios.get('/api/getFile', {
        params: { folderPath, fileName },
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue('Download failed');
    }
  }
);

export const deleteFileFromS3 = createAsyncThunk<void, { filePath: string }>('s3File/delete', async ({ filePath }, thunkAPI) => {
  try {
    await axios.delete('/api/delete', {
      params: { key: filePath },
    });
  } catch (error) {
    return thunkAPI.rejectWithValue('Delete failed');
  }
});

// Slice
export const s3FileSlice = createSlice({
  name: 's3File',
  initialState,
  reducers: {
    resetS3FileState() {
      return initialState;
    },
    setFilePath(state, action) {
      state.filePath = action.payload;
    },
    setFileBlob(state, action) {
      state.fileBlob = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(downloadFileFromS3.fulfilled, (state, action) => {
        state.fileBlob = action.payload;
      })
      .addMatcher(isPending(uploadFileToS3, downloadFileFromS3, deleteFileFromS3), state => {
        state.loading = true;
        state.errorMessage = null;
        state.updateSuccess = false;
      })
      .addMatcher(isFulfilled(uploadFileToS3, downloadFileFromS3, deleteFileFromS3), state => {
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isRejected(uploadFileToS3, downloadFileFromS3, deleteFileFromS3), (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload as string;
        state.updateSuccess = false;
      });
  },
});

export const { resetS3FileState, setFilePath, setFileBlob } = s3FileSlice.actions;

export default s3FileSlice.reducer;
